/**
 * Adapters: raw Sharma API JSON → table/graph shapes consumed by Content screens.
 * Contract not finalized — shapes below are best-effort; TODO align when OpenAPI/samples exist.
 */

import { chartColorAt } from "../config/chartColors";
import {
  DEFAULT_PERD,
  YTD_PERFORMANCE_BY_INDEX_DISPLAY_TITLE,
} from "../config/sharmaReportsConfig";
import { REASON_CODES } from "./sharmaReportMessages";
import {
  assertBusinessCycleScenarioShape,
  assertRiskAttributionThreeCategorySeries,
  assertValueAtRiskEnvelopeShape,
  assertYtdIndexIndexesShape,
  assertYtdRowsArrayShape,
  assertYtdStrategySharmaMapShape,
} from "./sharmaReportValidation";

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function buildYtdByIndexTableTitle(portfolioName, ctx = {}) {
  const base =
    ctx.defaultTitle || YTD_PERFORMANCE_BY_INDEX_DISPLAY_TITLE;
  const pname = portfolioName ? String(portfolioName) : "";
  return pname ? `${pname}: ${base}` : base;
}

function tableFail(reasonCode, ctx = {}, reasonDetail) {
  return {
    reasonCode,
    reasonDetail,
    title: String(ctx.defaultTitle || ctx.title || ""),
  };
}

function chartFail(reasonCode, ctx = {}, reasonDetail) {
  return tableFail(reasonCode, ctx, reasonDetail);
}

function emptyYtdTableResult(title, ctx, { includeAllocation, includeIndexId }) {
  const monthLabels = monthLabelsFromPerd(ctx.perd);
  return {
    title,
    columns: buildYtdColumns({ includeAllocation, includeIndexId, monthLabels }),
    rows: [],
    partial: true,
  };
}

function emptyAttributionChart(ctx, portfolioName) {
  const portfolio = portfolioName ? String(portfolioName) : "";
  const titleBase =
    String(ctx.defaultTitle || "Risk and Performance Attribution") +
    (portfolio ? ` — ${portfolio}` : "");
  return {
    title: titleBase,
    chartKind: "column",
    categories: ["% of Total Assets", "% of Total Risk", "% of Total Return"],
    series: [],
    extraOptions: buildAttributionExtraOptions([]),
    partial: true,
  };
}

const ATTRIBUTION_CATEGORIES = [
  "% of Total Assets",
  "% of Total Risk",
  "% of Total Return",
];

function resolveAttributionYAxisBounds(series) {
  const colCount = series.reduce(
    (max, s) => Math.max(max, Array.isArray(s.data) ? s.data.length : 0),
    0
  );
  let posMax = 0;
  let negMin = 0;
  let hasNegative = false;

  for (let j = 0; j < colCount; j++) {
    let posStack = 0;
    let negStack = 0;
    for (const s of series) {
      const v = num(s.data?.[j]);
      if (v == null) continue;
      if (v >= 0) {
        posStack += v;
      } else {
        negStack += v;
        hasNegative = true;
      }
    }
    if (posStack > posMax) posMax = posStack;
    if (negStack < negMin) negMin = negStack;
  }

  const dataMin = negMin;
  const dataMax = posMax > 0 ? posMax : 100;
  const span = Math.max(dataMax - dataMin, 1);
  const pad = span * 0.12;
  const yAxis = {
    min: dataMin < 0 ? dataMin - pad : 0,
    max: dataMax + pad,
  };
  if (hasNegative) {
    yAxis.plotLines = [{ value: 0, width: 1, color: "rgba(0,0,0,0.2)" }];
  }
  return yAxis;
}

function buildAttributionExtraOptions(series) {
  return {
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        pointPadding: 0.08,
        groupPadding: 0.12,
      },
    },
    yAxis: {
      title: { text: "%" },
      stackLabels: { enabled: true },
      ...resolveAttributionYAxisBounds(series),
    },
  };
}

function marginalRiskBetaTitle(ctx = {}) {
  const periodLabel = ctx.perd ? formatYyyyMmLabel(String(ctx.perd)) : "";
  const portfolioName =
    ctx.portfolioDisplayName != null
      ? String(ctx.portfolioDisplayName).trim()
      : "";
  if (portfolioName && periodLabel) {
    return `Sensitivity of ${portfolioName} as of ${periodLabel}`;
  }
  const fallback = String(
    ctx.defaultTitle || "Marginal Risk Beta"
  );
  return periodLabel ? `${fallback} (${periodLabel})` : fallback;
}

function marginalRiskBetaChartPayload(categories, series, ctx, { partial, partialNotice } = {}) {
  return {
    title: marginalRiskBetaTitle(ctx),
    chartKind: "column",
    categories,
    series,
    extraOptions: {
      chart: { polar: false },
      yAxis: {
        title: { text: "Beta (Up/Down)" },
        plotLines: [{ value: 0, width: 1, color: "rgba(0,0,0,0.15)" }],
      },
      plotOptions: {
        column: { grouping: true, borderWidth: 0 },
      },
      tooltip: { shared: true },
    },
    ...(partial ? { partial: true } : {}),
    ...(partialNotice ? { partialNotice } : {}),
  };
}

function hasYtdBlock(ytd) {
  if (!ytd || typeof ytd !== "object") return false;
  return (
    num(ytd.performance) != null ||
    num(ytd.stdev) != null ||
    num(ytd.sharpe_ratio) != null
  );
}

function hasLast3Block(l3) {
  if (!l3 || typeof l3 !== "object") return false;
  return Object.values(l3).some((v) => num(v) != null);
}

function strategyPayloadHasTableData(payload) {
  return hasYtdBlock(payload?.ytd) || hasLast3Block(payload?.last_3_months);
}

const YTD_COLUMN_FIELD = {
  No: "no",
  Fund: "fund",
  "Index ID": "indexId",
  "Allocation (%)": "allocation",
  Inception: "inception",
  Strategy: "strategy",
  "Performance (%)": "performance",
  "Standard Deviation (%)": "standardDeviation",
  "Sharpe Ratio": "sharpeRatio",
};

function ytdCellHasValue(row, column, monthLabels) {
  if (monthLabels.includes(column)) {
    const v = row.monthlyPerformance?.[column];
    return v != null && v !== "";
  }
  const field = YTD_COLUMN_FIELD[column];
  if (!field) return false;
  const v = row[field];
  return v != null && v !== "";
}

function filterEmptyYtdColumns(columns, rows, monthLabels) {
  const kept = columns.filter((col) =>
    rows.some((row) => ytdCellHasValue(row, col, monthLabels))
  );
  return kept.length > 0 ? kept : columns.filter((c) => c === "No" || c === "Fund");
}

function buildYtdStrategyRow(strategyName, payload, rowNo, portfolioInception, monthLabels) {
  const ytd = payload?.ytd || {};
  const nInv = num(payload?.num_investments);
  const fund =
    nInv != null
      ? `${strategyName} (${nInv} Investments)`
      : String(strategyName);
  return {
    no: rowNo,
    fund,
    allocation: num(payload?.total_allocation),
    inception: portfolioInception,
    strategy: strategyName,
    performance: num(ytd.performance),
    standardDeviation: num(ytd.stdev),
    sharpeRatio: num(ytd.sharpe_ratio),
    monthlyPerformance: monthlyPerformanceFromLast3Partial(
      payload?.last_3_months,
      monthLabels
    ),
  };
}

function buildYtdPortfolioRow(portfolio, rowNo, monthLabels) {
  const pytd = portfolio.ytd || {};
  return {
    no: rowNo,
    fund: String(portfolio.portfolio_name ?? "Portfolio"),
    allocation: num(portfolio.total_allocation),
    inception: formatInceptionShort(portfolio.inception),
    strategy: String(portfolio.strategy ?? ""),
    performance: num(pytd.performance),
    standardDeviation: num(pytd.stdev),
    sharpeRatio: num(pytd.sharpe_ratio),
    monthlyPerformance: monthlyPerformanceFromLast3Partial(
      portfolio.last_3_months,
      monthLabels
    ),
  };
}

function portfolioHasTableYtd(portfolio) {
  if (!portfolio || typeof portfolio !== "object") return false;
  return hasYtdBlock(portfolio.ytd) || hasLast3Block(portfolio.last_3_months);
}

function monthlyPerformanceFromLast3Partial(last3, monthLabels) {
  const out = {};
  if (!last3 || typeof last3 !== "object") return out;
  const keys = Object.keys(last3).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );
  keys.forEach((k, i) => {
    const label = monthLabels[i];
    const v = num(last3[k]);
    if (label != null && v != null) {
      out[label] = v;
    }
  });
  return out;
}

function ytdRowHasAnyData(row, monthLabels) {
  const cols = [
    "Fund",
    "Allocation (%)",
    "Performance (%)",
    "Standard Deviation (%)",
    "Sharpe Ratio",
    ...monthLabels,
  ];
  return cols.some((c) => ytdCellHasValue(row, c, monthLabels));
}

function pick(row, keys) {
  if (!row || typeof row !== "object") return undefined;
  for (const k of keys) {
    if (
      Object.prototype.hasOwnProperty.call(row, k) &&
      row[k] !== undefined &&
      row[k] !== null
    ) {
      return row[k];
    }
  }
  return undefined;
}

/** Unwrap common API envelopes. TODO: confirm with backend */
export function unwrapEnvelope(json) {
  if (json == null || typeof json !== "object") {
    return json;
  }
  return (
    json.data ??
    json.Data ??
    json.payload ??
    json.result ??
    json.body ??
    json
  );
}

function firstRowsArray(payload) {
  let u = unwrapEnvelope(payload);
  if (Array.isArray(u)) {
    return u;
  }
  u = unwrapEnvelope(u);
  if (Array.isArray(u)) {
    return u;
  }
  if (!u || typeof u !== "object") {
    return null;
  }
  const keys = ["rows", "items", "records", "results", "table", "lines"];
  for (const k of keys) {
    if (Array.isArray(u[k])) {
      return u[k];
    }
  }
  return null;
}

/** Last three month labels like Oct-09 from perd YYYYMM */
export function monthLabelsFromPerd(perd) {
  const p = perd || DEFAULT_PERD;
  const yy = p.length >= 6 ? p.slice(2, 4) : "09";
  return [`Oct-${yy}`, `Nov-${yy}`, `Dec-${yy}`];
}

function extractMonthly(row, monthLabels) {
  const out = {};
  const nested =
    pick(row, ["monthlyPerformance", "MonthlyPerformance", "months", "Months"]) ||
    row;
  for (const label of monthLabels) {
    const short = label.replace("-", "").toLowerCase();
    const candidates = [label, label.replace("-", ""), short];
    let v;
    for (const c of candidates) {
      if (nested && nested[c] !== undefined) {
        v = num(nested[c]);
        break;
      }
      if (row[c] !== undefined) {
        v = num(row[c]);
        break;
      }
    }
    if (v != null) {
      out[label] = v;
    }
  }
  return out;
}

function normalizeYtdRow(raw, index, { includeAllocation, includeIndexId }, monthLabels) {
  const row = raw && typeof raw === "object" ? raw : {};
  const monthlyPerformance = extractMonthly(row, monthLabels);
  const performance =
    num(
      pick(row, [
        "performance",
        "Performance",
        "ytdPerformance",
        "YtdPerformance",
        "yearToDatePerformance",
      ])
    ) ?? num(row.pct);
  const standardDeviation =
    num(pick(row, ["standardDeviation", "StandardDeviation", "stdDev", "volatility"])) ??
    num(row.std);
  const sharpeRatio =
    num(pick(row, ["sharpeRatio", "SharpeRatio", "sharpe"])) ?? num(row.sharpe);

  const out = {
    no: num(pick(row, ["no", "No", "rowNum", "index"])) ?? index + 1,
    fund: String(pick(row, ["fund", "Fund", "fundName", "FundName", "name"]) ?? ""),
    inception: String(pick(row, ["inception", "Inception", "startDate"]) ?? ""),
    strategy: String(pick(row, ["strategy", "Strategy", "assetClass"]) ?? ""),
    performance: performance ?? 0,
    standardDeviation: standardDeviation ?? 0,
    sharpeRatio: sharpeRatio ?? 0,
    monthlyPerformance,
  };

  if (includeIndexId) {
    out.indexId =
      num(pick(row, ["indexId", "IndexId", "index_id", "IndexID"])) ?? 0;
  }
  if (includeAllocation) {
    out.allocation =
      num(pick(row, ["allocation", "Allocation", "allocationPct", "weight"])) ?? 0;
  }

  return out;
}

function buildYtdColumns({
  includeAllocation,
  includeIndexId,
  monthLabels,
}) {
  return [
    "No",
    "Fund",
    ...(includeIndexId ? ["Index ID"] : []),
    ...(includeAllocation ? ["Allocation (%)"] : []),
    "Inception",
    "Strategy",
    "Performance (%)",
    "Standard Deviation (%)",
    "Sharpe Ratio",
    ...monthLabels,
  ];
}

/** "200910" → "Oct-09" (cabeceras mensuales de tabla YTD) */
function yyyymmToTableColumnLabel(ym) {
  const s = String(ym);
  if (s.length !== 6 || !/^\d{6}$/.test(s)) return s;
  const y = parseInt(s.slice(0, 4), 10);
  const mo = parseInt(s.slice(4, 6), 10);
  if (mo < 1 || mo > 12) return s;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[mo - 1]}-${String(y).slice(-2)}`;
}

/** Deriva etiquetas mensuales ordenadas desde last_3_months { YYYYMM: number } */
function monthLabelsFromLast3MonthsObject(last3) {
  if (!last3 || typeof last3 !== "object") {
    return monthLabelsFromPerd(DEFAULT_PERD);
  }
  const keys = Object.keys(last3).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );
  if (keys.length === 0) {
    return monthLabelsFromPerd(DEFAULT_PERD);
  }
  return keys.map(yyyymmToTableColumnLabel);
}

function monthlyPerformanceFromLast3(last3, monthLabels) {
  const out = {};
  if (!last3 || typeof last3 !== "object") return out;
  const keys = Object.keys(last3).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );
  keys.forEach((k, i) => {
    const label = monthLabels[i];
    if (label != null) {
      out[label] = num(last3[k]) ?? 0;
    }
  });
  return out;
}

/** ISO fecha corta → "Jan 04" / "Jan 70" */
function formatInceptionShort(iso) {
  if (iso == null || iso === "") return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (mo < 1 || mo > 12) return String(iso);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[mo - 1]} ${String(y).slice(-2)}`;
}

/**
 * @returns {{ title: string, columns: string[], rows: object[] }}
 */
export function adaptYtdPerformanceByStrategy(json, ctx = {}) {
  const u = unwrapEnvelope(json);

  if (
    u.strategies &&
    typeof u.strategies === "object" &&
    !Array.isArray(u.strategies)
  ) {
    try {
      assertYtdStrategySharmaMapShape(u);
    } catch {
      /* tolerant: build from whatever strategy entries are present */
    }
    const portfolioInception = formatInceptionShort(u.portfolio?.inception);
    let monthLabels = [];
    const stratEntries = Object.entries(u.strategies).sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]))
    );
    for (const [, payload] of stratEntries) {
      const l3 = payload?.last_3_months;
      if (l3 && typeof l3 === "object" && Object.keys(l3).length > 0) {
        monthLabels = monthLabelsFromLast3MonthsObject(l3);
        break;
      }
    }
    if (monthLabels.length === 0 && u.portfolio?.last_3_months) {
      monthLabels = monthLabelsFromLast3MonthsObject(u.portfolio.last_3_months);
    }
    if (monthLabels.length === 0) {
      monthLabels = monthLabelsFromPerd(ctx.perd);
    }

    const rows = [];
    let rowNo = 1;
    let partial = false;
    const skippedStrategies = stratEntries.filter(
      ([, payload]) => !strategyPayloadHasTableData(payload)
    );
    if (skippedStrategies.length > 0 && stratEntries.length > 0) {
      partial = true;
    }

    for (const [strategyName, payload] of stratEntries) {
      if (!strategyPayloadHasTableData(payload)) continue;
      const row = buildYtdStrategyRow(
        strategyName,
        payload,
        rowNo++,
        portfolioInception,
        monthLabels
      );
      if (ytdRowHasAnyData(row, monthLabels)) {
        rows.push(row);
      }
    }

    const hadStrategyRows = rows.length > 0;
    if (u.portfolio && typeof u.portfolio === "object" && portfolioHasTableYtd(u.portfolio)) {
      const pRow = buildYtdPortfolioRow(u.portfolio, rowNo, monthLabels);
      if (ytdRowHasAnyData(pRow, monthLabels)) {
        rows.push(pRow);
        if (!hadStrategyRows) {
          partial = true;
        }
      }
    }

    if (rows.length < 1) {
      const pname = u.portfolio?.portfolio_name
        ? String(u.portfolio.portfolio_name)
        : "";
      const title =
        (pname ? `${pname}: ` : "") + "YTD Performance by Strategy";
      return emptyYtdTableResult(
        String(u?.title || u?.reportTitle || title),
        ctx,
        { includeAllocation: true, includeIndexId: false }
      );
    }

    const pname = u.portfolio?.portfolio_name
      ? String(u.portfolio.portfolio_name)
      : "";
    const title =
      (pname ? `${pname}: ` : "") + "YTD Performance by Strategy";

    let columns = buildYtdColumns({
      includeAllocation: true,
      includeIndexId: false,
      monthLabels,
    });
    const colBefore = columns.length;
    columns = filterEmptyYtdColumns(columns, rows, monthLabels);
    if (columns.length < colBefore) {
      partial = true;
    }

    return {
      title: String(u?.title || u?.reportTitle || title),
      columns,
      rows,
      ...(partial ? { partial: true } : {}),
    };
  }

  const rowsRaw = firstRowsArray(json);
  try {
    assertYtdRowsArrayShape(rowsRaw);
  } catch {
    return emptyYtdTableResult(
      String(
        u?.title ||
          u?.reportTitle ||
          ctx.defaultTitle ||
          "YTD Performance by Strategy"
      ),
      ctx,
      { includeAllocation: true, includeIndexId: false }
    );
  }
  const monthLabels = monthLabelsFromPerd(ctx.perd);
  const rows = rowsRaw.map((r, i) =>
    normalizeYtdRow(r, i, { includeAllocation: true, includeIndexId: false }, monthLabels)
  );
  const columns = buildYtdColumns({
    includeAllocation: true,
    includeIndexId: false,
    monthLabels,
  });
  return {
    title:
      String(u?.title || u?.reportTitle || ctx.defaultTitle || "YTD Performance by Strategy"),
    columns,
    rows,
  };
}

export function adaptYtdPerformanceByIndex(json, ctx = {}) {
  const u = unwrapEnvelope(json);

  if (Array.isArray(u.indexes)) {
    if (u.indexes.length >= 1) {
      try {
        assertYtdIndexIndexesShape(u);
      } catch {
        /* tolerant: build from whatever index entries are present */
      }
    }

    let monthLabels = [];
    if (u.indexes.length >= 1) {
      monthLabels = monthLabelsFromLast3MonthsObject(u.indexes[0]?.last_3_months);
    }
    if (monthLabels.length === 0 && u.portfolio?.last_3_months) {
      monthLabels = monthLabelsFromLast3MonthsObject(u.portfolio.last_3_months);
    }
    if (monthLabels.length === 0) {
      monthLabels = monthLabelsFromPerd(ctx.perd);
    }

    let partial = u.indexes.length === 0;
    const rows = [];
    let rowNo = 1;

    if (u.indexes.length >= 1) {
      const skippedIndexes = u.indexes.filter(
        (item) => !strategyPayloadHasTableData(item)
      );
      if (skippedIndexes.length > 0) {
        partial = true;
      }
    }

    for (const item of u.indexes) {
      if (!strategyPayloadHasTableData(item)) {
        partial = true;
        continue;
      }
      const ytd = item?.ytd || {};
      const idxId = item?.index_id;
      const idNum =
        idxId !== undefined && idxId !== null && idxId !== ""
          ? Number(idxId)
          : NaN;
      const row = {
        no: rowNo++,
        fund: String(item?.index_name ?? ""),
        indexId: Number.isFinite(idNum) ? idNum : null,
        inception: formatInceptionShort(item?.inception),
        strategy: String(item?.strategy ?? ""),
        performance: num(ytd.performance),
        standardDeviation: num(ytd.stdev),
        sharpeRatio: num(ytd.sharpe_ratio),
        monthlyPerformance: monthlyPerformanceFromLast3Partial(
          item?.last_3_months,
          monthLabels
        ),
      };
      if (ytdRowHasAnyData(row, monthLabels)) {
        rows.push(row);
      } else {
        partial = true;
      }
    }

    const hadIndexRows = rows.length > 0;
    if (u.portfolio && typeof u.portfolio === "object" && portfolioHasTableYtd(u.portfolio)) {
      const pRow = buildYtdPortfolioRow(u.portfolio, rowNo, monthLabels);
      if (ytdRowHasAnyData(pRow, monthLabels)) {
        rows.push(pRow);
        if (!hadIndexRows) {
          partial = true;
        }
      }
    }

    if (rows.length < 1) {
      return emptyYtdTableResult(
        buildYtdByIndexTableTitle(u.portfolio?.portfolio_name, ctx),
        ctx,
        { includeAllocation: false, includeIndexId: true }
      );
    }

    const title = buildYtdByIndexTableTitle(u.portfolio?.portfolio_name, ctx);

    let columns = buildYtdColumns({
      includeAllocation: false,
      includeIndexId: true,
      monthLabels,
    });
    const colBefore = columns.length;
    columns = filterEmptyYtdColumns(columns, rows, monthLabels);
    if (columns.length < colBefore) {
      partial = true;
    }

    return {
      title,
      columns,
      rows,
      ...(partial ? { partial: true } : {}),
    };
  }

  const rowsRaw = firstRowsArray(json);
  try {
    assertYtdRowsArrayShape(rowsRaw);
  } catch {
    return emptyYtdTableResult(
      buildYtdByIndexTableTitle(u.portfolio?.portfolio_name, ctx),
      ctx,
      { includeAllocation: false, includeIndexId: true }
    );
  }
  const monthLabels = monthLabelsFromPerd(ctx.perd);
  const rows = rowsRaw.map((r, i) =>
    normalizeYtdRow(r, i, { includeAllocation: false, includeIndexId: true }, monthLabels)
  );
  const columns = buildYtdColumns({
    includeAllocation: false,
    includeIndexId: true,
    monthLabels,
  });
  return {
    title: buildYtdByIndexTableTitle(u.portfolio?.portfolio_name, ctx),
    columns,
    rows,
  };
}

/** "198805" → "May 88" for chart axis labels */
function formatYyyyMmLabel(ym) {
  const s = String(ym);
  if (s.length !== 6 || !/^\d{6}$/.test(s)) return s;
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(4, 6), 10);
  if (m < 1 || m > 12) return s;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const yy = String(y).slice(-2);
  return `${months[m - 1]} ${yy}`;
}

export function adaptDrawdownsChart(json, ctx = {}) {
  const u = unwrapEnvelope(json);
  let categories = [];
  let data = [];

  /** Sharma API: { drawdowns: { "198801": number, ... }, portfolio_id, portfolio_name } */
  const ddMap = u?.drawdowns;
  if (ddMap && typeof ddMap === "object" && !Array.isArray(ddMap)) {
    const keys = Object.keys(ddMap).sort((a, b) => {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
    let partial = false;
    const pairs = [];
    for (const k of keys) {
      const v = num(ddMap[k]);
      if (v == null) {
        partial = true;
        continue;
      }
      pairs.push({ k, v: Math.abs(v) });
    }
    if (pairs.length < 1) {
      partial = true;
      categories = keys.map((k) => formatYyyyMmLabel(k));
      data = keys.map(() => 0);
    } else {
      if (pairs.length < keys.length) {
        partial = true;
      }
      categories = pairs.map((p) => formatYyyyMmLabel(p.k));
      data = pairs.map((p) => p.v);
    }
    const portfolio = u.portfolio_name ? String(u.portfolio_name) : "";
    const titleBase =
      String(u?.title || ctx.defaultTitle || "Drawdowns Over Time") +
      (portfolio ? ` — ${portfolio}` : "");
    return {
      title: titleBase,
      chartKind: "column",
      categories,
      series: [{ name: "Drawdowns (%)", data }],
      extraOptions: {
        yAxis: { title: { text: "Drawdowns (%)" }, min: 0 },
        plotOptions: { column: { borderWidth: 0 } },
      },
      ...(partial ? { partial: true } : {}),
    };
  }

  if (u?.categories?.length && Array.isArray(u?.series?.[0]?.data)) {
    categories = u.categories.map(String);
    data = u.series[0].data.map((v) => num(v) ?? 0);
  } else if (Array.isArray(u?.series) && u.series[0]?.data) {
    categories =
      u.categories ||
      u.series[0].data.map((_, i) => String(i));
    data = u.series[0].data.map((v) => num(v) ?? 0);
  } else if (Array.isArray(u?.points)) {
    u.points.forEach((p) => {
      categories.push(String(p.date ?? p.period ?? p.label ?? ""));
      data.push(num(p.drawdown ?? p.value ?? p.pct ?? p.y) ?? 0);
    });
  } else if (Array.isArray(u)) {
    u.forEach((p, i) => {
      categories.push(String(p.date ?? p.x ?? p.period ?? i));
      data.push(num(p.drawdown ?? p.value ?? p.y) ?? 0);
    });
  } else {
    return chartFail(REASON_CODES.UNKNOWN_FORMAT, {
      defaultTitle: ctx.defaultTitle || "Drawdowns Over Time",
    });
  }

  const finitePairs = categories
    .map((cat, i) => ({ cat, v: num(data[i]) }))
    .filter((p) => p.v != null);
  if (finitePairs.length < 1) {
    if (categories.length >= 1) {
      return {
        title: String(u?.title || ctx.defaultTitle || "Drawdowns Over Time"),
        chartKind: "column",
        categories,
        series: [{ name: "Drawdowns (%)", data: categories.map(() => 0) }],
        extraOptions: {
          yAxis: { title: { text: "Drawdowns (%)" } },
          plotOptions: { column: { borderWidth: 0 } },
        },
        partial: true,
      };
    }
    return chartFail(REASON_CODES.NO_DRAWDOWN_DATA, {
      defaultTitle: ctx.defaultTitle || "Drawdowns Over Time",
    });
  }
  const partial = finitePairs.length < categories.length;
  categories = finitePairs.map((p) => p.cat);
  data = finitePairs.map((p) => Math.abs(p.v));

  return {
    title: String(u?.title || ctx.defaultTitle || "Drawdowns Over Time"),
    chartKind: "column",
    categories,
    series: [{ name: "Drawdowns (%)", data }],
    extraOptions: {
      yAxis: { title: { text: "Drawdowns (%)" } },
      plotOptions: { column: { borderWidth: 0 } },
    },
    ...(partial ? { partial: true } : {}),
  };
}

/**
 * Equity beta / marginal risk beta — GET `/api/risk-metrics/marginal-risk-beta`.
 *
 * Contrato v2: `risk_factors[]` con `beta_up` / `beta_down` por benchmark (valores crudos, sin ×100).
 * Contrato v1 (fallback): tres escalares en la raíz.
 * Gráfico: columnas agrupadas Up (azul) / Down (rojo) por benchmark; eje Y centrado en 0.
 * `beta_undiff`, `significance` y `pnl_impact` no se dibujan (no están en el mock con datos actuales).
 */
export function adaptMarginalRiskBetaChart(json, ctx = {}) {
  const u = unwrapEnvelope(json);

  if (Array.isArray(u?.risk_factors) && u.risk_factors.length > 0) {
    const factors = u.risk_factors.filter(
      (rf) => rf && typeof rf === "object" && !Array.isArray(rf)
    );
    if (factors.length < 1) {
      return chartFail(REASON_CODES.MARGINAL_RISK_BETA_EMPTY, {
        defaultTitle: ctx.defaultTitle || "Equity Beta (Marginal Risk Beta)",
      });
    }
    const categories = factors.map((rf) =>
      String(
        rf.risk_factor_name ??
          rf.name ??
          rf.risk_factor_id ??
          "Benchmark"
      ).trim() || "Benchmark"
    );
    const upData = [];
    const downData = [];
    let partial = false;
    for (const rf of factors) {
      const bUp = num(rf.beta_up);
      const bDown = num(rf.beta_down);
      if (bUp == null || bDown == null) {
        partial = true;
      }
      upData.push(bUp ?? null);
      downData.push(bDown ?? null);
    }
    const allZero = factors.every(
      (rf) =>
        (num(rf.beta_up) ?? 0) === 0 && (num(rf.beta_down) ?? 0) === 0
    );
    if (allZero) {
      partial = true;
    }
    const series = [
      {
        name: "Beta up",
        type: "column",
        data: upData,
        color: chartColorAt(0),
      },
      {
        name: "Beta down",
        type: "column",
        data: downData,
        color: chartColorAt(1),
      },
    ];
    return marginalRiskBetaChartPayload(categories, series, ctx, { partial });
  }

  const up = num(u.beta_up);
  const down = num(u.beta_down);
  const undiff = num(u.beta_undiff);
  if (up == null && down == null && undiff == null) {
    return chartFail(REASON_CODES.MARGINAL_RISK_BETA_EMPTY, {
      defaultTitle: ctx.defaultTitle || "Equity Beta (Marginal Risk Beta)",
    });
  }
  const partial = [up, down].filter((v) => v == null).length > 0;
  const categories = ["Portafolio"];
  const series = [];
  if (up != null) {
    series.push({
      name: "Beta up",
      type: "column",
      data: [up],
      color: chartColorAt(0),
    });
  }
  if (down != null) {
    series.push({
      name: "Beta down",
      type: "column",
      data: [down],
      color: chartColorAt(1),
    });
  }
  return marginalRiskBetaChartPayload(categories, series, ctx, { partial });
}

/**
 * Risk Profile (spider/radar) — GET `/api/risk-metrics/risk-profile` (misma base que `SHARMA_REPORT_PATHS.riskProfile` en `sharmaReportsConfig.js`).
 *
 * **Contrato:** raíz (o envelope) con claves `"1"`…`"6"`; cada una `{ name: string, value: number }`.
 * **Orden de ejes:** estable por clave numérica ascendente (1 → 6), alineado al orden de negocio del backend.
 * **Escala radial (eje Y):** dinámica: `max = max(values) * 1.12`, con piso 1 si todos los valores son ≤ 0.
 */
export function adaptRiskProfileRadarChart(json, ctx = {}) {
  const u = unwrapEnvelope(json);
  const keys = ["1", "2", "3", "4", "5", "6"];
  const categories = [];
  const data = [];
  let partial = false;
  for (const k of keys) {
    const cell = u[k];
    if (!cell || typeof cell !== "object" || Array.isArray(cell)) {
      partial = true;
      continue;
    }
    const name = cell.name ?? cell.Name;
    if (name == null || String(name).trim() === "") {
      partial = true;
      continue;
    }
    const v = num(cell.value ?? cell.Value);
    if (v == null) {
      partial = true;
    }
    categories.push(String(name));
    data.push(v != null ? v : 0);
  }
  if (categories.length < 1) {
    return chartFail(REASON_CODES.RISK_PROFILE_EMPTY, {
      defaultTitle: ctx.defaultTitle || "Risk Profile",
    });
  }
  const maxVal = Math.max(...data, 0);
  const yMax =
    maxVal <= 0 ? 1 : Math.ceil(maxVal * 100 * 1.12) / 100;

  const portfolio = u.portfolio_name ? String(u.portfolio_name) : "";
  const perdLabel = ctx.perd ? ` (${formatYyyyMmLabel(String(ctx.perd))})` : "";
  const titleBase =
    String(u?.title || ctx.defaultTitle || "Risk Profile") +
    (portfolio ? ` — ${portfolio}` : "") +
    perdLabel;

  return {
    title: titleBase,
    chartKind: "polar",
    categories,
    series: [
      {
        name: "Risk profile",
        type: "line",
        data,
        pointPlacement: "on",
      },
    ],
    extraOptions: {
      chart: { polar: true },
      pane: { size: "85%" },
      xAxis: {
        tickmarkPlacement: "on",
        lineWidth: 0,
      },
      yAxis: {
        gridLineInterpolation: "polygon",
        lineWidth: 0,
        min: 0,
        max: yMax,
        tickAmount: 5,
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          dataLabels: {
            enabled: true,
            format: "{y:.2f}",
          },
        },
        line: {
          marker: { enabled: true, radius: 4 },
        },
      },
      tooltip: { shared: true, valueDecimals: 2 },
    },
    ...(partial ? { partial: true } : {}),
  };
}

export function adaptRiskAttributionChart(json, ctx = {}) {
  const u = unwrapEnvelope(json);

  if (Array.isArray(u?.strategies) && u.strategies.length === 0) {
    return emptyAttributionChart(ctx, u.portfolio_name);
  }

  /** Sharma API: { strategies: [{ strategy_name, allocation_pct, risk_pct, performance_pct, ... }], portfolio_name, ... } */
  if (Array.isArray(u?.strategies) && u.strategies.length >= 1) {
    const sample = u.strategies[0];
    const hasSharmaShape =
      sample &&
      typeof sample === "object" &&
      ("strategy_name" in sample ||
        "allocation_pct" in sample ||
        "risk_pct" in sample);

    if (hasSharmaShape) {
      const categories = ATTRIBUTION_CATEGORIES;
      let partial = false;
      const series = [];
      for (const s of u.strategies) {
        const a = num(s.allocation_pct);
        const r = num(s.risk_pct);
        const p = num(s.performance_pct);
        if (a == null && r == null && p == null) {
          partial = true;
          series.push({
            name: String(s.strategy_name ?? s.name ?? "Strategy"),
            type: "column",
            data: [null, null, null],
          });
          continue;
        }
        if (a == null || r == null || p == null) {
          partial = true;
        }
        series.push({
          name: String(s.strategy_name ?? s.name ?? "Strategy"),
          type: "column",
          data: [a ?? null, r ?? null, p ?? null],
        });
      }
      if (series.length < 1) {
        return emptyAttributionChart(ctx, u.portfolio_name);
      }
      const portfolio = u.portfolio_name ? String(u.portfolio_name) : "";
      const titleBase =
        String(u?.title || ctx.defaultTitle || "Risk and Performance Attribution") +
        (portfolio ? ` — ${portfolio}` : "");
      return {
        title: titleBase,
        chartKind: "column",
        categories,
        series,
        extraOptions: buildAttributionExtraOptions(series),
        ...(partial ? { partial: true } : {}),
      };
    }
  }

  if (Array.isArray(u?.series) && u.categories?.length === 3) {
    try {
      assertRiskAttributionThreeCategorySeries(u);
    } catch {
      return emptyAttributionChart(ctx, u.portfolio_name);
    }
    let partial = false;
    const series = u.series.map((s) => {
      const raw = Array.isArray(s.data) ? s.data.map((v) => num(v)) : [];
      if (raw.length !== 3 || raw.some((v) => v == null)) {
        partial = true;
      }
      return {
        name: String(s.name ?? s.label ?? "Series"),
        type: "column",
        data: raw.map((v) => v ?? null),
      };
    });
    return {
      title: String(u?.title || ctx.defaultTitle || "Risk and Performance Attribution"),
      chartKind: "column",
      categories: u.categories.map(String),
      series,
      extraOptions: buildAttributionExtraOptions(series),
      ...(partial ? { partial: true } : {}),
    };
  }

  const strategies =
    u.strategies ||
    u.breakdown ||
    u.rows ||
    u.positions ||
    firstRowsArray(u);
  if (!Array.isArray(strategies) || strategies.length < 1) {
    return emptyAttributionChart(ctx, u.portfolio_name);
  }

  const metricAliasGroups = [
    ["pctAssets", "assets", "pct_assets", "assetPct", "Assets"],
    ["pctRisk", "risk", "pct_risk", "riskPct", "Risk"],
    ["pctReturn", "ret", "return", "pct_return", "returnPct", "Return"],
  ];

  const categories = ["% of Total Assets", "% of Total Risk", "% of Total Return"];
  let partial = false;
  const series = [];
  for (const s of strategies) {
    const a = num(pick(s, metricAliasGroups[0]));
    const r = num(pick(s, metricAliasGroups[1]));
    const p = num(pick(s, metricAliasGroups[2]));
    if (a == null && r == null && p == null) {
      partial = true;
      series.push({
        name: String(
          pick(s, ["name", "strategy", "label", "fund", "Fund"]) ?? "Position"
        ),
        type: "column",
        data: [null, null, null],
      });
      continue;
    }
    if (a == null || r == null || p == null) {
      partial = true;
    }
    series.push({
      name: String(
        pick(s, ["name", "strategy", "label", "fund", "Fund"]) ?? "Position"
      ),
      type: "column",
      data: [a ?? null, r ?? null, p ?? null],
    });
  }
  if (series.length < 1) {
    return emptyAttributionChart(ctx, u.portfolio_name);
  }

  return {
    title: String(u?.title || ctx.defaultTitle || "Risk and Performance Attribution"),
    chartKind: "column",
    categories,
    series,
    extraOptions: buildAttributionExtraOptions(series),
    ...(partial ? { partial: true } : {}),
  };
}

/**
 * Value at Risk — GET `/api/risk-metrics/value-at-risk` (vía proxy `/api/sharma-reports/...`).
 * Contrato Bruno: RyseNet `value-at-risk.bru` o README RyseNet cuando exista.
 *
 * v1: tabla plana { title, columns, rows }; datos pobres → partial + aviso, no loadError duro.
 * TODO v2: cabeceras agrupadas, columna Strategy con fondo, Marginal VAR negativos en rojo (si tableContent / grid lo permiten sin reescritura).
 */
function buildValueAtRiskTableFromEnvelope(u, ctx, titleBase) {
  const portfolio = u.portfolio;
  const funds = u.funds;

  const errText =
    portfolio.error != null && String(portfolio.error).trim() !== ""
      ? String(portfolio.error).trim()
      : null;

  const allColumns = [
    "No",
    "Fund",
    "Allocation (%)",
    "Inception",
    "Strategy",
    "Incremental VAR (%)",
    "Component VAR (MM)",
    "Marginal VAR (MM)",
    "Assets invested (MM)",
  ];

  const VAR_COL_FIELD = {
    "Allocation (%)": "allocation",
    "Inception": "inception",
    "Strategy": "strategy",
    "Incremental VAR (%)": "incrementalVarPct",
    "Component VAR (MM)": "componentVarMm",
    "Marginal VAR (MM)": "marginalVarMm",
    "Assets invested (MM)": "assetsInvestedMm",
  };

  function varCellHasValue(row, col) {
    if (col === "No" || col === "Fund") return false;
    const field = VAR_COL_FIELD[col];
    const v = field ? row[field] : null;
    return v != null && v !== "";
  }

  function filterEmptyVarColumns(columns, rows) {
    return columns.filter((col) => {
      if (col === "No" || col === "Fund") return true;
      return rows.some((row) => varCellHasValue(row, col));
    });
  }

  const round2 = (v) =>
    v == null || !Number.isFinite(v) ? null : Math.round((v + Number.EPSILON) * 100) / 100;

  const formatInceptionCell = (raw) => {
    if (raw == null || raw === "") return "";
    const s = String(raw);
    if (/^\d{6}$/.test(s)) {
      return formatYyyyMmLabel(s);
    }
    return formatInceptionShort(s);
  };

  let partial = Boolean(errText);
  let partialNotice = errText || undefined;
  const rows = funds.map((f, i) => ({
    no: i + 1,
    fund: String(pick(f, ["fund_name", "FundName", "name"]) ?? f.fund_id ?? ""),
    allocation: round2(num(f.allocation)),
    inception: formatInceptionCell(pick(f, ["inception", "Inception"])),
    strategy: String(pick(f, ["strategy", "Strategy"]) ?? ""),
    incrementalVarPct: round2(
      num(pick(f, ["incremental_var", "incrementalVar", "IncrementalVar"]))
    ),
    componentVarMm: round2(
      num(pick(f, ["component_var", "componentVar", "ComponentVar"]))
    ),
    marginalVarMm: round2(
      num(pick(f, ["marginal_var", "marginalVar", "MarginalVar"]))
    ),
    assetsInvestedMm: round2(
      num(pick(f, ["assets_invested", "assetsInvested", "AssetsInvested"]))
    ),
  }));

  const sumAlloc = funds.reduce((s, f) => s + (num(f.allocation) ?? 0), 0);
  const totalAllocation =
    num(portfolio.total_allocation ?? portfolio.allocation) != null
      ? round2(num(portfolio.total_allocation ?? portfolio.allocation))
      : round2(sumAlloc);

  const periodLabel =
    portfolio.start_period && portfolio.end_period
      ? `${formatYyyyMmLabel(String(portfolio.start_period))}–${formatYyyyMmLabel(String(portfolio.end_period))}`
      : formatInceptionCell(portfolio.inception);

  const totalRow = {
    no: funds.length + 1,
    fund: String(
      pick(portfolio, ["portfolio_name", "PortfolioName"]) ??
        portfolio.portfolio_id ??
        "Portfolio"
    ),
    allocation: totalAllocation,
    inception: periodLabel,
    strategy: "",
    incrementalVarPct: round2(
      num(pick(portfolio, ["incremental_var", "incrementalVar"]))
    ),
    componentVarMm: round2(num(pick(portfolio, ["component_var", "componentVar"]))),
    marginalVarMm: round2(num(pick(portfolio, ["marginal_var", "marginalVar"]))),
    assetsInvestedMm: round2(
      num(pick(portfolio, ["assets_invested", "assetsInvested"]))
    ),
  };
  rows.push(totalRow);

  if (!funds.length) {
    partial = true;
  }

  let columns = allColumns;
  const colBefore = columns.length;
  columns = filterEmptyVarColumns(columns, rows);
  if (columns.length < colBefore) {
    partial = true;
  }
  if (columns.length < 2) {
    columns = ["No", "Fund"];
    partial = true;
  }

  const pname = portfolio.portfolio_name
    ? String(portfolio.portfolio_name)
    : "";
  const conf = num(portfolio.confidence_level);
  const titleSuffix =
    conf != null && conf > 0 && conf <= 1
      ? ` (${Math.round(conf * 100)}% conf.)`
      : "";
  const title =
    String(u?.title || u?.reportTitle || ctx.defaultTitle || "") ||
    (pname ? `${pname}: ${titleBase}${titleSuffix}` : `${titleBase}${titleSuffix}`);

  return {
    title,
    columns,
    rows,
    ...(partial ? { partial: true } : {}),
    ...(partialNotice ? { partialNotice } : {}),
  };
}

export function adaptValueAtRiskTable(json, ctx = {}) {
  const u = unwrapEnvelope(json);
  try {
    assertValueAtRiskEnvelopeShape(u);
  } catch (e) {
    return tableFail(REASON_CODES.UNKNOWN_FORMAT, ctx, e?.message);
  }
  return buildValueAtRiskTableFromEnvelope(u, ctx, "Value at Risk");
}

export function adaptMonteCarloValueAtRiskTable(json, ctx = {}) {
  const u = unwrapEnvelope(json);
  try {
    assertValueAtRiskEnvelopeShape(u);
  } catch (e) {
    return tableFail(REASON_CODES.UNKNOWN_FORMAT, ctx, e?.message);
  }
  return buildValueAtRiskTableFromEnvelope(u, ctx, "Monte Carlo Value at Risk");
}

function normalizeBusinessCycleScenarioBlock(json) {
  if (json == null || typeof json !== "object") {
    return json;
  }
  if (Array.isArray(json.scenarios) && json.scenarios.length > 0) {
    return json.scenarios[0];
  }
  if (Array.isArray(json.data)) {
    return json;
  }
  const u = unwrapEnvelope(json);
  if (u && typeof u === "object" && !Array.isArray(u)) {
    if (Array.isArray(u.scenarios) && u.scenarios.length > 0) {
      return u.scenarios[0];
    }
    if (Array.isArray(u.data)) {
      return u;
    }
  }
  return u;
}

export function adaptBusinessCycleScenarioTable(json, ctx = {}) {
  const block = normalizeBusinessCycleScenarioBlock(json);
  try {
    assertBusinessCycleScenarioShape(block);
  } catch (e) {
    return tableFail(REASON_CODES.UNKNOWN_FORMAT, ctx, e?.message);
  }

  const round2 = (v) =>
    v == null || !Number.isFinite(v) ? null : Math.round((v + Number.EPSILON) * 100) / 100;

  const formatInceptionCell = (raw) => {
    if (raw == null || raw === "") return "";
    const s = String(raw);
    if (/^\d{6}$/.test(s)) {
      return formatYyyyMmLabel(s);
    }
    return formatInceptionShort(s);
  };

  const columns = [
    "No",
    "Fund",
    "Allocation (%)",
    "Inception",
    "Strategy",
    "Min Performance (%)",
    "Med Performance (%)",
    "Max Performance (%)",
  ];

  const rows = block.data.map((item, i) => ({
    no: i + 1,
    fund: String(pick(item, ["name", "fund_name", "FundName"]) ?? ""),
    allocation: round2(num(item.allocation)),
    inception: formatInceptionCell(pick(item, ["inception", "Inception"])),
    strategy: String(pick(item, ["strategy", "Strategy"]) ?? ""),
    minPerformancePct: round2(
      num(pick(item, ["min_performance", "minPerformance", "MinPerformance"]))
    ),
    medPerformancePct: round2(
      num(pick(item, ["med_performance", "medPerformance", "MedPerformance"]))
    ),
    maxPerformancePct: round2(
      num(pick(item, ["max_performance", "maxPerformance", "MaxPerformance"]))
    ),
  }));

  let partial = !block.data.length;
  let partialNotice;
  const warnings = block.warnings;
  if (Array.isArray(warnings) && warnings.length > 0) {
    partial = true;
    partialNotice = warnings.map((w) => String(w)).join("; ");
  }

  const title =
    String(block.title || ctx.defaultTitle || "") || "Business Cycle Scenario";
  const scenario = block.scenario ? String(block.scenario) : undefined;

  return {
    title,
    columns,
    rows,
    ...(scenario ? { scenario } : {}),
    ...(partial ? { partial: true } : {}),
    ...(partialNotice ? { partialNotice } : {}),
  };
}

export function adaptTableReport(reportKey, json, ctx) {
  try {
    switch (reportKey) {
      case "ytdByStrategy":
        return adaptYtdPerformanceByStrategy(json, ctx);
      case "ytdByIndex":
        return adaptYtdPerformanceByIndex(json, ctx);
      case "valueAtRisk":
        return adaptValueAtRiskTable(json, ctx);
      case "mcValueAtRisk":
        return adaptMonteCarloValueAtRiskTable(json, ctx);
      case "businessCycleScenario":
        return adaptBusinessCycleScenarioTable(json, ctx);
      default:
        return tableFail(REASON_CODES.UNKNOWN_FORMAT, ctx, reportKey);
    }
  } catch (e) {
    return tableFail(REASON_CODES.UNKNOWN_FORMAT, ctx, e?.message);
  }
}
